import React, { useCallback } from 'react';
import { FlatList, StyleSheet, TouchableOpacity } from 'react-native';

import { LoadingSkeleton } from '@/components/SkeletonLoader';
import { Text } from '@/components/TextComponent';
import { Box, Image, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { designSystem } from '@/styles/styles';
import { toLocaleCurrency } from '@/utils/numeric/currency';

import { RipsRecentPack } from '../hooks/use-recent-packs';
import { RIPS_ROW_PADDING, useRipsRowItemWidth } from '../hooks/use-rips-row-item-width';

const CARD_HEIGHT = 88;
const CARD_RADIUS = 16;
const CARD_GAP = 12;
const TITLE_LINE_HEIGHT = 24;
const SKELETON_TITLE_WIDTH = 100;
const SKELETON_CARDS = 3;

const TEXTS_PADDING_LEFT = 16;

// Pack artwork tilted on the right side of the card (Figma: 56×96 image rotated 15°, clipped by a 68×87 frame).
const IMAGE_FRAME_WIDTH = 68;
const IMAGE_FRAME_HEIGHT = 87;
const PACK_IMAGE_WIDTH = 56;
const PACK_IMAGE_HEIGHT = 96;
const PACK_IMAGE_LEFT = 9;
const PACK_IMAGE_TOP = 9;

const keyExtractor = (pack: RipsRecentPack) => pack.vendorId;

type RipAgainCardProps = {
    pack: RipsRecentPack;
    width: number;
    onPress: (pack: RipsRecentPack) => void;
};

const RipAgainCard = ({ pack, width, onPress }: RipAgainCardProps) => (
    <TouchableOpacity
        onPress={() => onPress(pack)}
        activeOpacity={0.8}
        style={[styles.card, { width }]}
        testID={`ripsRipAgainCard-${pack.vendorId}`}
    >
        <Box style={styles.imageFrame}>
            <Image source={{ uri: pack.thumbnailUrl }} style={styles.image} resizeMode="cover" />
        </Box>
        <Box gap="s2">
            <Box>
                <Text variant="labelLarge2" color={ds.colorToken.text.primary} numberOfLines={1}>
                    {pack.category}
                </Text>
                {pack.badge ? (
                    <Text variant="bodySmall2" color={ds.colorToken.text.secondary} numberOfLines={1}>
                        {pack.badge}
                    </Text>
                ) : null}
            </Box>
            <Text variant="labelLarge2" color={ds.colorToken.text.primary} numberOfLines={1}>
                {toLocaleCurrency(pack.priceCents / 100, false)}
            </Text>
        </Box>
    </TouchableOpacity>
);

const RipAgainSkeleton = ({ cardWidth }: { cardWidth: number }) => (
    <Box pb="s32" testID="ripsRipAgainSkeleton">
        <Box style={styles.title}>
            <LoadingSkeleton
                width={SKELETON_TITLE_WIDTH}
                height={TITLE_LINE_HEIGHT}
                borderRadius="r8"
                backgroundColor={ds.colorToken.background.neutral.subtler}
                skeletonColor={designSystem.colors.neutral300}
            />
        </Box>
        <Row style={[styles.list, styles.skeletonRow]}>
            {[...Array(SKELETON_CARDS)].map((_, index) => (
                <LoadingSkeleton
                    key={index}
                    width={cardWidth}
                    height={CARD_HEIGHT}
                    style={styles.skeletonCard}
                    backgroundColor={ds.colorToken.background.neutral.subtler}
                    skeletonColor={designSystem.colors.neutral300}
                />
            ))}
        </Row>
    </Box>
);

type RipsRipAgainProps = {
    packs: RipsRecentPack[];
    isLoading?: boolean;
    onPackPress: (pack: RipsRecentPack) => void;
};

/**
 * "Rip again" section of the Rips lobby: the packs the player ripped most recently. Hidden when they have none.
 */
export const RipsRipAgain = ({ packs, isLoading, onPackPress }: RipsRipAgainProps) => {
    const cardWidth = useRipsRowItemWidth(CARD_GAP);

    const renderItem = useCallback(
        ({ item }: { item: RipsRecentPack }) => <RipAgainCard pack={item} width={cardWidth} onPress={onPackPress} />,
        [cardWidth, onPackPress]
    );

    if (isLoading) {
        return <RipAgainSkeleton cardWidth={cardWidth} />;
    }

    if (packs.length === 0) {
        return null;
    }

    return (
        <Box pb="s32">
            <Text
                variant="headlineSmall"
                lineHeight={TITLE_LINE_HEIGHT}
                color={ds.colorToken.text.primary}
                style={styles.title}
            >
                {'Rip again'}
            </Text>
            <FlatList
                data={packs}
                keyExtractor={keyExtractor}
                renderItem={renderItem}
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={styles.list}
            />
        </Box>
    );
};

const styles = StyleSheet.create({
    title: {
        marginHorizontal: RIPS_ROW_PADDING,
        marginBottom: 16,
    },
    list: {
        paddingHorizontal: RIPS_ROW_PADDING,
        gap: CARD_GAP,
    },
    card: {
        height: CARD_HEIGHT,
        justifyContent: 'center',
        paddingLeft: TEXTS_PADDING_LEFT,
        paddingRight: IMAGE_FRAME_WIDTH,
        borderRadius: CARD_RADIUS,
        borderCurve: 'continuous',
        borderWidth: 1,
        borderColor: ds.colorValue.border.subtle,
        backgroundColor: ds.colorValue.background.neutral.subtler,
        overflow: 'hidden',
    },
    imageFrame: {
        position: 'absolute',
        top: 0,
        right: 0,
        width: IMAGE_FRAME_WIDTH,
        height: IMAGE_FRAME_HEIGHT,
        overflow: 'hidden',
    },
    image: {
        position: 'absolute',
        left: PACK_IMAGE_LEFT,
        top: PACK_IMAGE_TOP,
        width: PACK_IMAGE_WIDTH,
        height: PACK_IMAGE_HEIGHT,
        transform: [{ rotate: '15deg' }],
    },
    skeletonRow: {
        overflow: 'hidden',
    },
    skeletonCard: {
        borderRadius: CARD_RADIUS,
    },
});
